import type { ReactNode } from "react"

export interface StatBorderGridItem {
  /** The headline value — big, black, tight-tracking (e.g. "89", "AHAM", "$0") */
  value: string
  /** Bold label under the value */
  label: string
  /** Small uppercase note below the label. Accepts ReactNode so callers can embed inline icons / tooltips. */
  note?: ReactNode
}

interface StatBorderGridProps {
  /** Exactly 4 items for the 2×2 layout. Rendered left→right, top→bottom. */
  stats: StatBorderGridItem[]
  /**
   * Index of the cell that gets a faint orange tint background
   * (visual anchor). Default 0 (top-left). Set to -1 to disable.
   */
  highlightIndex?: number
  /** Additional classes on the outer grid */
  className?: string
}

/**
 * StatBorderGrid — 2×2 grid of headline stats inside a hard black border,
 * with 1px black dividers between every cell. Editorial treatment used in
 * the `/tools` hero and other full-page heroes that want a confident,
 * magazine-style stat block.
 *
 * Use with exactly 4 stats. Values scale from 28px (mobile) → 44px (desktop).
 */
export function StatBorderGrid({
  stats,
  highlightIndex = 0,
  className = "",
}: StatBorderGridProps) {
  return (
    <div className={`grid grid-cols-2 border border-gray-900 rounded-md overflow-hidden ${className}`}>
      {stats.slice(0, 4).map((s, i) => {
        const borderRight = i % 2 === 0 ? "border-r border-gray-900" : ""
        const borderBottom = i < 2 ? "border-b border-gray-900" : ""
        const bg = i === highlightIndex ? "bg-[#ff9951]/[0.06]" : "bg-white"
        return (
          <div
            key={i}
            className={`px-4 sm:px-5 py-4 sm:py-[22px] ${borderRight} ${borderBottom} ${bg}`}
          >
            <div
              className="font-black tracking-[-0.04em] text-gray-900 leading-none"
              style={{ fontSize: "clamp(28px, 3vw, 44px)" }}
            >
              {s.value}
            </div>
            <div className="text-[13px] font-bold text-gray-900 mt-2 sm:mt-2.5">{s.label}</div>
            {s.note && (
              <div className="text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-gray-500 mt-1">
                {s.note}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
